import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, of, Subscription, timer } from 'rxjs';
import { catchError, exhaustMap, filter, throttleTime, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FRONTEND_BUILD_OPTIONS, FrontendBuild, isConfirmedBuildMismatch, isAppVersion } from './frontend-build';

@Injectable({ providedIn: 'root' })
export class FrontendUpdateService implements OnDestroy {
  private readonly required = new BehaviorSubject(false);
  readonly updateRequired$ = this.required.asObservable();
  private subscription: Subscription;
  private readonly http: HttpClient;

  constructor(backend: HttpBackend, @Inject(FRONTEND_BUILD_OPTIONS) private readonly build: FrontendBuild) {
    // Anonymous endpoint: bypass auth/error interceptors so failed checks stay quiet.
    this.http = new HttpClient(backend);
  }

  start(): void {
    if (this.subscription || !isAppVersion(this.build.id)) { return; }
    const root = ((environment as any).planningToolApiEndpointAddress || '')
      .replace(/\/pt4\/?$/, '').replace(/\/+$/, '');
    if (!root) { return; }
    this.subscription = merge(
      timer(0, 180000),
      fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden)),
      fromEvent(window, 'focus'),
      fromEvent(window, 'pageshow')
    ).pipe(
      throttleTime(1000),
      exhaustMap(() => this.http.get(root + '/version', {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        params: { updateCheck: String(Date.now()) }
      }).pipe(timeout(10000), catchError(() => of(null))))
    ).subscribe(response => {
      // Latch per tab. An error or a later response must never acknowledge a confirmed block.
      if (!this.required.value && isConfirmedBuildMismatch(this.build.id, response)) {
        this.required.next(true);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) { this.subscription.unsubscribe(); }
  }
}
